import type { HierarchyRectangularNode } from 'd3-hierarchy';
import { useMemo, Fragment } from 'react';

import { useCurrentTheme } from '../../../core/hooks/useCurrentTheme.js';
import {
  CLUSTER_LABEL_OFFSET_X,
  CLUSTER_LABEL_OFFSET_Y,
  DARK_LABEL_TEXT,
  LIGHT_LABEL_TEXT,
  MIN_CLUSTER_HEIGHT,
} from '../constants.js';
import { TreeMapClusterBorder } from './canvas/TreeMapClusterBorder.js';
import { TreeMapNodeLabel } from './canvas/TreeMapNodeLabel.js';
import type { LegendItem } from '../../core/components/legend/categorical/types/legend.js';
import {
  type HighlightingMap,
  type InternalTreeMapNode,
} from '../types/internal.js';
import { getClusterPosition } from '../utils/get-cluster-position.js';
import { getNodeColor } from '../utils/get-node-color.js';
import { getNodeDimension } from '../utils/get-node-dimension.js';
import { getNodeOpacity } from '../utils/get-node-opacity.js';

export interface TreeMapClusterProps {
  treeMapData: HierarchyRectangularNode<InternalTreeMapNode>;
  highlightedItem?: LegendItem;
  highlightingMap: HighlightingMap;
}

export const TreeMapCluster = (props: TreeMapClusterProps) => {
  const { treeMapData, highlightedItem, highlightingMap } = props;

  const theme = useCurrentTheme();

  const clusterNodes = useMemo(
    () =>
      treeMapData
        .descendants()
        .reverse()
        .filter((entry) => entry.depth === 1),
    [treeMapData],
  );

  return clusterNodes.map((clusterNode) => {
    const { x0, y0 } = getClusterPosition(clusterNode);
    const { width, height } = getNodeDimension(clusterNode);
    const labelColor = theme === 'light' ? DARK_LABEL_TEXT : LIGHT_LABEL_TEXT;
    const borderColor = getNodeColor(clusterNode);

    const opacity = getNodeOpacity(
      clusterNode,
      highlightingMap,
      highlightedItem,
    );

    const doesLabelFit = height >= MIN_CLUSTER_HEIGHT;

    return (
      <Fragment key={`${clusterNode.data.name}}`}>
        <TreeMapClusterBorder
          x={x0}
          y={y0}
          color={borderColor}
          width={width}
          height={height}
          opacity={opacity}
        />
        {doesLabelFit && (
          <TreeMapNodeLabel
            x={x0 + width / 2}
            y={y0 + CLUSTER_LABEL_OFFSET_Y}
            maxWidth={width - CLUSTER_LABEL_OFFSET_X}
            label={clusterNode.data.name}
            color={labelColor}
            opacity={opacity}
          />
        )}
      </Fragment>
    );
  });
};
